import AES from 'crypto-js/aes';
import encoderUtf8 from 'crypto-js/enc-utf8';
import { HDNodeWallet, Wallet, Signer } from 'ethers';

import { ChainConfig } from '#/config/configType';
import { WALLET_PRIVATE_KEY_CHARS } from '#/config/constants';

import { getAnonymousRPCProvider } from './utils';

// Removed in March 2021. We should be able to safely remove after longer period,
// but that would make users who didn't accessed wallet until that removed from LS
// See also 'WalletUpgraderForLegacyPrivateKey'
const LEGACY_WALLET_SECRET = 'IdX.148293123521xTd.232134123525';

/**
 * Temporary secret used only for connect wallet, after wallet-user association
 * this secret is no longer used because we can get proper user wallet secret
 */
export const UNLOCK_WALLET_SECRET = 'unlock-secret';

// Investigate replacement by native WebCrypto https://developer.mozilla.org/en-US/docs/Web/API/Web_Crypto_API
// https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto/encrypt

/**
 * Decript existing legacy wallet with legacy static key
 * and encrypt with new provided key
 */
// export const migrateLegacyWallet = async (
//   encryptedWallet: string,
//   newPersonalSecret: string
// ) => {
//   const wallet = await decryptWallet(encryptedWallet, LEGACY_WALLET_SECRET);
//   return encryptWallet(wallet, newPersonalSecret);
// };

export const encryptWallet = async (
  wallet: Wallet | HDNodeWallet,
  secret: string
) => {
  // return wallet.encrypt(PASSWORD);
  // Official keystore encryption (above) is too slow
  // So we just use AES to encrypt private key
  return AES.encrypt(wallet.privateKey, secret).toString();
};

export const decryptWallet = async (
  encryptedKey: string,
  secret: string,
  chainOverride?: ChainConfig
) => {
  // return await Wallet.fromEncryptedJson(
  //   fileContent: encryptedKey,
  //   password || SECRET
  // );
  const privateKey = AES.decrypt(encryptedKey, secret).toString(encoderUtf8);
  return new Wallet(privateKey, getAnonymousRPCProvider(chainOverride));
};

// (window as any).debugAesEncrypt = (text, pass) => AES.encrypt(text, pass).toString();
// (window as any).debugAesDecrypt = (text, pass) => AES.decrypt(text, pass).toString(encoderUtf8);

export const unlockByEncryptedKey = async (
  encryptedKey: string,
  encryptionSecret: string,
  chainOverride?: ChainConfig
) => {
  if (!encryptedKey) {
    throw new Error('Please unlock your wallet.');
  }
  const wallet = await decryptWallet(
    encryptedKey,
    encryptionSecret,
    chainOverride
  );
  return {
    publicKey: wallet.address.toLowerCase(),
    encryptedKey,
    signMessage: (message: string | Uint8Array) => wallet.signMessage(message),
    wallet,
  };
};

export const unlockByKeystoreFile = async (
  input: {
    encryptedJson: string;
    walletPassword?: string;
  },
  personalSecret: string
) => {
  const wallet = await Wallet.fromEncryptedJson(
    input.encryptedJson,
    input.walletPassword || LEGACY_WALLET_SECRET
  );
  const encryptedKey = await encryptWallet(wallet, personalSecret);
  return {
    publicKey: wallet.address.toLowerCase(),
    encryptedKey,
    personalSecret,
    // signMessage: (message: string | Uint8Array) => wallet.signMessage(message),
  };
};

// https://docs.ethers.io/ethers.js/html/api-wallet.html#encrypted-json-wallets
export const unlockPrivateKeyWallet = async (
  privateKeyParam: string,
  personalSecret: string
) => {
  // Add hex / "0x" prefix automatically
  const privateKey = privateKeyParam.startsWith('0x')
    ? privateKeyParam
    : `0x${privateKeyParam}`;

  // PK is 64 Bytes + 2 Bytes for "0x"
  if (privateKey.length !== WALLET_PRIVATE_KEY_CHARS) {
    throw new Error('Private key is not valid.');
  }
  const wallet = new Wallet(privateKey.trim(), getAnonymousRPCProvider());
  const encryptedKey = await encryptWallet(wallet, personalSecret);
  return {
    publicKey: wallet.address.toLowerCase(),
    encryptedKey,
    personalSecret,
    // signMessage: (message: string | Uint8Array) => wallet.signMessage(message),
  };
};

/**
 * https://docs.ethers.io/ethers.js/html/api-wallet.html
 *
 * @param mnemonic eg. address close cradle sheriff settle race they naive decade pupil refuse toss"
 */
export const unlockByMnemonicPhrase = async (
  mnemonic: string,
  personalSecret: string
) => {
  const wallet = Wallet.fromPhrase(mnemonic.trim());
  const encryptedKey = await encryptWallet(wallet, personalSecret);
  return {
    publicKey: wallet.address,
    encryptedKey,
    personalSecret,
  };
};

export const getSigner = async (
  encryptedPrivateKey: string,
  personalSecret: string,
  chainOverride?: ChainConfig
): Promise<Signer> => {
  const wallet = await unlockByEncryptedKey(
    encryptedPrivateKey,
    personalSecret,
    chainOverride
  );
  return wallet.wallet;
};

// export const signText = async (
//   encryptedPrivateKey: string,
//   text: string,
//   secret?: string
// ): Promise<string> => {
//   // const safeText = '\u0019' + text;
//   const signer = await getSigner(encryptedPrivateKey, secret);
//   return signer.signMessage(text);
// };

// // https://docs.ethers.io/ethers.js/html/api-wallet.html#signing
// export const signHash = async (
//   encryptedPrivateKey: string,
//   hash: string,
//   secret?: string
// ): Promise<string> => {
//   const signer = await getSigner(encryptedPrivateKey, secret);
//   return signer.signMessage(getBytes(hash));
// };

export const generateRandomWallet = async () => {
  return Wallet.createRandom();
};
